<template>
  <v-navigation-drawer
    v-model="glossaryStore.isOpen"
    temporary
    :location="display.mobile.value ? 'bottom' : 'right'"
    width="400"
    aria-labelledby="glossary-title"
    style="z-index: 9999 !important"
  >
    <v-toolbar density="default" bg-color="transparent" class="py-0 px-4">
      <h3 class="pt-4 mt-6 text-secondary" id="glossary-title">Glossary</h3>
      <v-spacer></v-spacer>
      <div class="close-btn">
        <span
          class="text-caption mr-2 close-text text-uppercase"
          @click="glossaryStore.closeGlossary"
          role="button"
          tabindex="0"
          @keydown.enter.space.prevent="glossaryStore.closeGlossary"
          >Close</span
        >
        <v-btn
          icon
          variant="elevated"
          color="primary"
          @click="glossaryStore.closeGlossary"
          aria-label="Close glossary"
          size="small"
        >
          <v-icon color="white" size="20">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-toolbar>

    <v-container class="pa-4">
      <div class="d-flex align-center justify-space-between dropdown-container">
        <div class="d-flex search-container">
          <v-text-field
            v-model="searchQuery"
            density="compact"
            variant="outlined"
            placeholder="Search glossary terms"
            hide-details
            class="search-field"
            aria-label="Search glossary terms"
            @keydown.enter="handleSearch"
            @input="debouncedSearch"
            :loading="glossaryStore.loading"
            clearable
            @click:clear="handleClearSearch"
          ></v-text-field>
          <v-btn
            icon
            variant="elevated"
            color="primary"
            class="search-btn"
            aria-label="Submit search"
            @click="handleSearch"
            :loading="glossaryStore.loading"
          >
            <v-icon color="white">mdi-magnify</v-icon>
          </v-btn>
        </div>

        <div class="custom-dropdown" @click.stop ref="dropdownRef">
          <button
            class="dropdown-button"
            @click="toggleDropdown"
            @keydown.enter.space.prevent="toggleDropdown"
            :aria-expanded="dropdownOpen"
            aria-label="Sort glossary terms"
          >
            <span>{{ selectedSortOption }}</span>
            <v-icon
              :class="{ 'rotate-180': dropdownOpen }"
              class="dropdown-icon"
            >
              mdi-chevron-down
            </v-icon>
          </button>
          <div
            v-if="dropdownOpen"
            class="dropdown-menu"
            @click.stop="handleDropdownClick"
          >
            <div
              v-for="option in sortOptions"
              :key="option.value"
              class="dropdown-item"
              :class="{ active: selectedSortOption === option.value }"
              @click.stop.prevent="selectOption(option.value)"
              @mouseup.stop="selectOption(option.value)"
              @touchend.stop.prevent="selectOption(option.value)"
              @pointerup.stop="selectOption(option.value)"
              @keydown.enter.prevent="selectOption(option.value)"
              :data-value="option.value"
              tabindex="0"
              role="option"
            >
              {{ option.title }}
            </div>
          </div>
        </div>
      </div>

      <!-- Letter Filter Chips -->
      <div v-if="glossaryStore.sortedLetters.length > 0" class="mt-4 mb-2">
        <v-chip-group
          v-model="selectedLetterIndex"
          selected-class="text-primary"
          @update:model-value="handleLetterFilter"
          class="letter-filter-chips"
        >
          <v-chip
            v-for="letter in glossaryStore.sortedLetters"
            :key="letter"
            :value="letter"
            size="small"
            variant="outlined"
            :color="
              glossaryStore.selectedLetter === letter ? 'primary' : 'default'
            "
          >
            {{ letter }}
          </v-chip>
        </v-chip-group>
      </div>

      <!-- Loading State -->
      <div
        v-if="
          glossaryStore.loading &&
          (glossaryStore.glossaryData?.length || 0) === 0
        "
        class="text-center py-8"
      >
        <v-progress-circular
          indeterminate
          color="primary"
          size="48"
        ></v-progress-circular>
        <p class="text-caption mt-2">Loading glossary...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="glossaryStore.error" class="text-center py-8">
        <v-alert
          type="error"
          :text="glossaryStore.error"
          class="mb-4"
        ></v-alert>
        <v-btn
          color="primary"
          @click="handleRetry"
          :loading="glossaryStore.loading"
        >
          Retry
        </v-btn>
      </div>

      <!-- Results Info -->
      <div
        v-else-if="glossaryStore.total > 0"
        class="d-flex align-center justify-space-between mb-2 results-info-section"
      >
        <span class="text-caption text-medium-emphasis">
          {{ glossaryStore.glossaryData?.length || 0 }} of
          {{ glossaryStore.total }} terms
        </span>
        <v-btn
          v-if="glossaryStore.searchQuery || glossaryStore.selectedLetter"
          variant="text"
          size="small"
          @click="handleClearFilters"
          class="text-caption clear-filters-btn"
        >
          Clear Filters
        </v-btn>
      </div>

      <!-- No Results -->
      <div
        v-else-if="
          !glossaryStore.loading &&
          (glossaryStore.glossaryData?.length || 0) === 0
        "
        class="text-center py-8"
      >
        <p class="text-body-2 text-medium-emphasis">No glossary terms found.</p>
        <v-btn
          v-if="glossaryStore.searchQuery || glossaryStore.selectedLetter"
          variant="text"
          @click="handleClearFilters"
          class="mt-2"
        >
          Clear Filters
        </v-btn>
      </div>

      <!-- Glossary Items -->
      <div
        v-if="glossaryStore.glossaryData?.length > 0"
        class="glossary-list-scroll"
      >
        <v-list lines="two" class="pa-0 mt-6">
          <template v-for="letter in glossaryStore.sortedLetters" :key="letter">
            <div
              v-if="glossaryStore.groupedGlossaryItems[letter]"
              class="glossary-item mb-4"
            >
              <div class="letter-header mb-4 pl-4">
                <h1 class="text-secondary" role="heading" aria-level="2">
                  {{ letter }}
                </h1>
              </div>
              <template
                v-for="item in glossaryStore.groupedGlossaryItems[letter]"
                :key="item.slug"
              >
                <!-- Main term display -->
                <v-list-item
                  :class="[
                    'mb-8 rounded',
                    { 'glossary-item-active': isItemActive(item) },
                    { 'glossary-item-exact-match': isExactMatch(item) },
                  ]"
                  variant="outlined"
                  :data-term="item.slug"
                >
                  <div class="d-flex flex-column">
                    <div class="term-content">
                      <!-- Single term display -->
                      <div class="term-title">
                        {{ item.name || item.title }}
                      </div>
                      <div v-html="item.content || item.summary"></div>
                      <div
                        v-if="item.subject"
                        class="text-caption text-secondary mt-1"
                      >
                        Subject: {{ item.subject.title || item.subject.name }}
                      </div>
                      <div
                        v-if="item.grades"
                        class="text-caption text-secondary"
                      >
                        Grades: {{ item.grades }}
                      </div>
                    </div>
                    <v-btn
                      v-if="hasLearnMoreLink(item)"
                      variant="text"
                      append-icon="mdi-chevron-right"
                      @click="viewDefinition(item)"
                      class="glossary-btn align-self-start"
                    >
                      Learn More
                    </v-btn>
                  </div>
                </v-list-item>
              </template>
            </div>
          </template>
        </v-list>

        <!-- Load More Button -->
        <div v-if="canLoadMore" class="text-center mt-4 mb-4">
          <v-btn
            @click="handleLoadMore"
            :loading="glossaryStore.loading"
            variant="outlined"
            color="primary"
          >
            Load More
          </v-btn>
        </div>
      </div>
    </v-container>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
  import { useDisplay } from 'vuetify';
  import { useGlossaryStore } from '../../store';
  import { ref, computed, onMounted, onUnmounted, watch } from 'vue';

  const display = useDisplay();
  const glossaryStore = useGlossaryStore();

  // Local state
  const searchQuery = ref('');
  const selectedSortOption = ref('A-Z');
  const selectedLetterIndex = ref<string | null>(null);
  const dropdownOpen = ref(false);
  const dropdownRef = ref<HTMLElement | null>(null);

  // Initialize sort option based on current store state
  const initializeSortOption = () => {
    const currentDirection = glossaryStore.sortDirection;
    selectedSortOption.value = currentDirection === 'asc' ? 'A-Z' : 'Z-A';
  };

  // Sort options
  const sortOptions = [
    { title: 'A-Z', value: 'A-Z' },
    { title: 'Z-A', value: 'Z-A' },
  ];

  // Computed properties
  const canLoadMore = computed(() => {
    const currentLength = glossaryStore.glossaryData?.length || 0;
    const total = glossaryStore.total;
    const loading = glossaryStore.loading;

    // Very strict conditions for showing load more:
    // 1. Must have actual data loaded
    // 2. Must not be loading
    // 3. Total must be significantly higher than current length (buffer for API inconsistencies)
    // 4. Current length must be a reasonable pagination size (suggests we're paginating)
    const hasValidData = currentLength > 0;
    const notLoading = !loading;
    const hasSignificantlyMoreData = total > currentLength + 5; // Buffer of 5 items
    const isReasonablePageSize = currentLength >= 10; // Suggests we loaded at least one page

    const result =
      hasValidData &&
      notLoading &&
      hasSignificantlyMoreData &&
      isReasonablePageSize;

    return result;
  });

  // Local debounce function
  const useDebounceFn = (fn: (...args: any[]) => void, delay: number) => {
    let timeout: number | null = null;
    return (...args: any[]) => {
      if (timeout !== null) clearTimeout(timeout);
      timeout = window.setTimeout(() => {
        fn(...args);
        timeout = null;
      }, delay);
    };
  };

  // Debounced search function - reduced timing for better responsiveness
  const debouncedSearch = useDebounceFn((event: Event) => {
    const target = event.target as HTMLInputElement;
    if (target.value !== glossaryStore.searchQuery) {
      glossaryStore.searchGlossary(target.value);
    }
  }, 200); // Reduced from 300ms to 200ms

  // Event handlers
  const handleSearch = () => {
    glossaryStore.searchGlossary(searchQuery.value);
  };

  const handleClearSearch = () => {
    searchQuery.value = '';
    glossaryStore.searchGlossary('');
  };

  const handleSortChange = (value: string) => {
    if (!value) {
      return;
    }

    const order = 'alpha';
    const direction = value === 'A-Z' ? 'asc' : 'desc';

    try {
      glossaryStore.changeSortOrder(order, direction);
    } catch (error) {
      console.error('Error in changeSortOrder:', error);
    }
  };

  const handleLetterFilter = (value: string | null) => {
    if (value) {
      glossaryStore.filterByLetter(value);
      selectedLetterIndex.value = value;
    } else {
      glossaryStore.filterByLetter('');
      selectedLetterIndex.value = null;
    }
  };

  const handleClearFilters = () => {
    searchQuery.value = '';
    selectedLetterIndex.value = null;
    glossaryStore.clearFilters();
  };

  const handleLoadMore = () => {
    // Double-check before making the request
    if (canLoadMore.value) {
      glossaryStore.loadMoreData();
    }
  };

  const handleRetry = () => {
    glossaryStore.fetchGlossaryData();
  };

  // Custom dropdown handlers with debouncing
  let toggleTimeout: number | null = null;
  let clickOutsideEnabled = ref(true);

  const toggleDropdown = () => {
    // Clear any existing timeout
    if (toggleTimeout) {
      clearTimeout(toggleTimeout);
    }

    // Temporarily disable click outside handler
    clickOutsideEnabled.value = false;

    // Add delay to prevent rapid toggling
    toggleTimeout = window.setTimeout(() => {
      dropdownOpen.value = !dropdownOpen.value;
      toggleTimeout = null;

      // Re-enable click outside handler after a delay
      setTimeout(() => {
        clickOutsideEnabled.value = true;
      }, 100);
    }, 10);
  };

  const selectOption = (value: string) => {
    // Prevent multiple rapid calls
    if (selectedSortOption.value === value && dropdownOpen.value === false) {
      return;
    }

    selectedSortOption.value = value;

    // Close dropdown with a small delay to ensure event processing
    setTimeout(() => {
      dropdownOpen.value = false;
    }, 50);

    handleSortChange(value);
  };

  const handleDropdownClick = (event: Event) => {
    event.stopPropagation();
    event.preventDefault();
  };

  // Close dropdown when clicking outside
  const handleClickOutside = (event: Event) => {
    if (!clickOutsideEnabled.value) {
      return;
    }

    if (dropdownOpen.value && dropdownRef.value) {
      const target = event.target as HTMLElement;
      if (!dropdownRef.value.contains(target)) {
        dropdownOpen.value = false;
      }
    }
  };

  // Helper functions
  const isItemActive = (item: any) => {
    return glossaryStore.currentTerm === item.slug;
  };

  const isExactMatch = (item: any) => {
    // Highlight exact matches whenever we have a search query
    if (!glossaryStore.searchQuery) {
      return false;
    }

    const searchTerm = glossaryStore.searchQuery.toLowerCase();
    const itemName = (item.name || item.title || '').toLowerCase();
    const itemSlug = (item.slug || '').toLowerCase();

    // Check if this item is an exact match for the current search term
    return itemSlug === searchTerm || itemName === searchTerm;
  };

  const hasLearnMoreLink = (item: any) => {
    // Check if item has additional information that warrants a "Learn More" link
    return item.details && item.details.length > 0;
  };

  const viewDefinition = (item: any) => {
    // Handle learn more action - could navigate to full definition page
    console.log('View full definition for:', item.slug);
    // TODO: Implement navigation to full definition or expand inline
  };

  // Initialize glossary on component mount
  onMounted(() => {
    glossaryStore.initializeGlossary();
    initializeSortOption();
    // Add click outside listener
    document.addEventListener('click', handleClickOutside);
  });

  // Cleanup on unmount
  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
  });

  // Watch for currentTerm changes to highlight the active term
  watch(
    () => glossaryStore.currentTerm,
    (newTerm) => {
      if (newTerm) {
        // Scroll to the term if it exists in the current data
        const termElement = document.querySelector(`[data-term="${newTerm}"]`);
        if (termElement) {
          termElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    },
  );

  // Sync local search query with store
  watch(
    () => glossaryStore.searchQuery,
    (newQuery) => {
      searchQuery.value = newQuery;
    },
  );

  // Watch for isOpen changes
  watch(
    () => glossaryStore.isOpen,
    (newIsOpen) => {
      // Handle drawer open/close state changes
    },
  );

  // Watch for glossaryData changes
  watch(
    () => glossaryStore.glossaryData,
    (newData) => {
      // Data updated, UI will automatically reflect changes
    },
  );

  // Watch for sort order changes from store
  watch(
    () => [glossaryStore.sortOrder, glossaryStore.sortDirection],
    ([order, direction]) => {
      const newSortOption = direction === 'asc' ? 'A-Z' : 'Z-A';
      if (selectedSortOption.value !== newSortOption) {
        selectedSortOption.value = newSortOption;
      }
    },
    { immediate: true },
  );
</script>

<style lang="scss" scoped>
  .close-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }

  .v-toolbar {
    background-color: transparent !important;
    background: transparent !important;
    border-bottom: 1px solid rgb(var(--v-theme-outline-variant));
  }

  .v-toolbar .v-toolbar__content {
    background-color: transparent !important;
    background: transparent !important;
  }

  .close-text {
    cursor: pointer;
    color: rgb(var(--v-theme-secondary));
    font-weight: 500;
  }

  .search-container {
    width: 100%;
    max-width: 280px;
    align-items: stretch;
    margin-right: 16px;
  }

  .search-field {
    flex: 1;
    margin-right: 0;
    background-color: #fff;

    :deep(.v-field) {
      border-radius: 4px 0 0 4px;
    }
  }

  .search-btn {
    width: 40px;
    height: 40px;
    background-color: rgb(var(--v-theme-primary));
    border-radius: 0 4px 4px 0;
    margin-left: -1px;
  }

  .small-select {
    width: 80px;
    min-width: 80px;
    height: 40px;

    :deep(.v-field) {
      height: 40px;
    }

    :deep(.v-field__input) {
      min-height: 40px;
    }
  }

  // Custom dropdown styles
  .custom-dropdown {
    position: relative;
    width: 80px;
    min-width: 80px;
    height: 40px;
  }

  .dropdown-button {
    width: 100%;
    height: 40px;
    background-color: white;
    border: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
    border-radius: 4px;
    padding: 0 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s ease;

    &:hover {
      border-color: rgb(var(--v-theme-primary));
    }

    &:focus {
      outline: none;
      border-color: rgb(var(--v-theme-primary));
      box-shadow: 0 0 0 2px rgba(var(--v-theme-primary), 0.2);
    }

    span {
      color: rgb(var(--v-theme-secondary));
      font-size: 14px;
      font-weight: 400;
    }
  }

  .dropdown-icon {
    transition: transform 0.2s ease;
    font-size: 16px;
    color: rgb(var(--v-theme-secondary));
  }

  .rotate-180 {
    transform: rotate(180deg);
  }

  .dropdown-menu {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: white !important;
    border: 2px solid rgba(var(--v-border-color), var(--v-border-opacity)) !important;
    border-radius: 4px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2) !important;
    z-index: 10000 !important;
    margin-top: 2px;
    overflow: hidden;
    min-height: 80px;
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
  }

  .dropdown-item {
    padding: 12px 16px;
    cursor: pointer;
    font-size: 14px;
    color: rgb(var(--v-theme-secondary));
    transition: background-color 0.2s ease;
    user-select: none;
    pointer-events: auto !important;
    position: relative;
    z-index: 1001;

    &:hover,
    &:focus {
      background-color: rgba(var(--v-theme-primary), 0.08);
      outline: none;
    }

    &.active {
      background-color: rgba(var(--v-theme-primary), 0.12);
      color: rgb(var(--v-theme-primary));
      font-weight: 500;
    }
  }

  .letter-filter-chips {
    max-height: 100px;
    overflow-y: auto;
  }

  .glossary-list-scroll {
    height: calc(100vh - 200px);
    overflow-y: auto;
    padding-bottom: 2rem;
  }

  .glossary-item {
    .letter-header {
      position: sticky;
      top: 0;
      background-color: rgb(var(--v-theme-surface));
      z-index: 1;
      padding: 0.5rem 1rem;
      margin: 0 -1rem;
      border-bottom: 1px solid rgb(var(--v-theme-outline-variant));
    }

    .v-list-item {
      padding: 1rem;
      border: 1px solid rgb(var(--v-theme-outline-variant));

      &.glossary-item-active {
        background-color: rgb(var(--v-theme-primary-container));
        border-color: rgb(var(--v-theme-primary));
      }

      &.glossary-item-exact-match {
        background-color: #ffffff !important;
        border: 1px solid rgb(var(--v-theme-primary)) !important;
        border-radius: 8px !important;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

        // Add a subtle animation to draw attention
        animation: exactMatchGlow 2s ease-in-out;

        // If item is both exact match and active, enhance the styling
        &.glossary-item-active {
          border-width: 2px;
          box-shadow: 0 4px 8px rgba(var(--v-theme-primary), 0.2);
        }
      }
    }
  }

  .term-content {
    width: 100%;

    .term-title {
      font-weight: 700;
      margin-bottom: 0.5rem;
      color: rgb(var(--v-theme-on-surface));
    }
  }

  .glossary-btn {
    margin-top: 1rem;
    color: rgb(var(--v-theme-primary));
    font-weight: 500;
    text-transform: none;

    &:hover {
      background-color: rgb(var(--v-theme-primary-container));
    }
  }

  :deep(.v-chip-group) {
    .v-chip {
      margin: 2px;
    }
  }

  :deep(.v-navigation-drawer__content) {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  // Ensure Results Info section doesn't interfere
  .results-info-section {
    isolation: isolate;
  }

  // Add spacing to prevent dropdown overlap
  .dropdown-container {
    margin-bottom: 20px;
    position: relative;
    z-index: 10;
  }

  .results-info-section {
    margin-top: 16px;
    position: relative;
    z-index: 5;
  }

  // Fix z-index for Clear Filters button to be below dropdown menu
  .clear-filters-btn {
    z-index: 1 !important;
    position: relative;
  }

  // Animation for exact match highlighting
  @keyframes exactMatchGlow {
    0% {
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
    50% {
      box-shadow: 0 4px 12px rgba(var(--v-theme-primary), 0.3);
    }
    100% {
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
  }
</style>
